// src/pages/Archive/index.jsx
import React, { useState } from 'react';
import { Tag, Empty, Typography, Space, Button, Modal, message, Pagination } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { usePostContext } from '../../App';
import './index.css';
import 'antd/dist/reset.css';

const { Title: AntTitle, Text } = Typography;

const Archive = () => {
  const { posts, loggedInUser, setPosts } = usePostContext();
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState({ visible: false, post: null });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5; // 每页显示5篇文章

  // 过滤当前用户的文章
  const userPosts = posts.filter(post => post.author === loggedInUser?.username);

  // 按「年-月」分组文章
  const groupPostsByMonth = () => {
    if (!userPosts.length) return {};
    const groups = {};
    userPosts.forEach(post => {
      const date = new Date(post.publishTime);
      const monthKey = `${date.getFullYear()}年${date.getMonth() + 1}月`;
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(post);
    });
    return Object.entries(groups).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  };

  const groupedPosts = groupPostsByMonth();

  // 编辑文章
  const handleEdit = (post) => {
    navigate('/write', { state: { post } });
  };

  // 删除文章
  const handleDelete = () => {
    if (deleteModal.post) {
      setPosts(prev => prev.filter(post => post.id !== deleteModal.post.id));
      message.success('文章已删除');
      setDeleteModal({ visible: false, post: null });
    }
  };

  // 显示删除确认
  const showDeleteConfirm = (post) => {
    setDeleteModal({ visible: true, post });
  };

  // 处理页码变化
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="archive-container">
      <AntTitle level={1} className="archive-title">文章归档</AntTitle>
      
      {!loggedInUser ? (
        <div className="archive-not-login">
          <Empty 
            description={<Text type="secondary" style={{ fontSize: '16px' }}>请登录后查看文章归档</Text>}
            style={{ marginBottom: '20px' }}
          />
          <Link to="/login">
            <Button type="primary">立即登录</Button>
          </Link>
        </div>
      ) : userPosts.length === 0 ? (
        <div className="archive-no-posts">
          <Empty 
            description="暂无归档文章" 
            style={{ margin: '50px 0' }}
          />
          <Link to="/write">
            <Button type="primary">撰写第一篇文章</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="archive-month-list">
            {groupedPosts.map(([month, postsInMonth]) => {
              // 根据当前页计算显示哪些文章
              const startIndex = (currentPage - 1) * pageSize;
              const endIndex = startIndex + pageSize;
              
              const filteredPosts = postsInMonth.filter((_, index) => {
                const globalIndex = groupedPosts.slice(0, groupedPosts.findIndex(m => m[0] === month))
                  .reduce((acc, [, posts]) => acc + posts.length, 0) + index;
                return globalIndex >= startIndex && globalIndex < endIndex;
              });

              if (filteredPosts.length === 0) return null;

              return (
                <div className="archive-month-group" key={month}>
                  <div className="archive-month-title">{month}</div>
                  <div className="archive-posts-in-month">
                    {filteredPosts.map((post) => (
                      <div className="archive-post-item" key={post.id}>
                        <div className="archive-post-header">
                          <Link 
                            to={`/postdetail/${post.id}`} 
                            className="archive-post-title"
                          >
                            {post.title}
                          </Link>
                          {post.category && (
                            <Tag color="blue" className="archive-category-tag">
                              {post.category}
                            </Tag>
                          )}
                        </div>
                        
                        <div className="archive-post-meta">
                          <Text type="secondary" style={{ fontSize: '14px' }}>
                            {post.publishTime} · 作者：{post.author} · 👁️{post.views || 0} · 👍{post.likeCount || 0}
                          </Text>
                          <Space size="small">
                            {Array.isArray(post.tags) 
                              ? post.tags.map(tag => <Tag key={tag} size="small">{tag}</Tag>) 
                              : <Tag size="small">{post.tags}</Tag>
                            }
                          </Space>
                        </div>
                        
                        <div className="archive-post-actions">
                          <Button 
                            size="small" 
                            onClick={() => handleEdit(post)}
                            style={{ marginRight: 8 }}
                          >
                            编辑
                          </Button>
                          <Button 
                            size="small" 
                            danger
                            onClick={() => showDeleteConfirm(post)}
                          >
                            删除
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 分页组件 */}
          {userPosts.length > pageSize && (
            <div className="archive-pagination">
              <Pagination
                current={currentPage}
                total={userPosts.length}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total) => `共 ${total} 篇文章`}
              />
            </div>
          )}
        </>
      )}

      {/* 删除确认弹窗 */}
      <Modal
        title="确认删除"
        open={deleteModal.visible}
        onOk={handleDelete}
        onCancel={() => setDeleteModal({ visible: false, post: null })}
        okText="确认"
        cancelText="取消"
      >
        <p>确定要删除文章 "{deleteModal.post?.title}" 吗？此操作不可恢复。</p>
      </Modal>
    </div>
  );
};

export default Archive;